import { Link, useLocation } from "react-router-dom";
import { themeFor } from "../../lib/theme";
import { Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { TelegramButton, TelegramIcon } from "./TelegramButton";
import { LOGO, CONTACT } from "../../data/content";
import { SOLUTION_DETAILS } from "../../data/solutions";
import { SERVICES } from "../../data/services";
import { LEGAL_PAGES, LEGAL_ORDER } from "../../data/legal";

const COMPANY = [
  { label: "About Us", to: "/about" },
  { label: "Brands We Deal With", to: "/brands-we-deal-with" },
  { label: "Technology", to: "/technology" },
  { label: "Projects", to: "/projects" },
  { label: "Insights", to: "/insights" },
  { label: "FAQ", to: "/faq" },
  { label: "Careers", to: "/careers" },
  { label: "Downloads", to: "/downloads" },
  { label: "Support", to: "/support" },
  { label: "Contact", to: "/contact" },
  { label: "Inquiries", to: "/inquiries" },
  { label: "Payment Receipts", to: "/receipts" },
  { label: "Request a Quote", to: "/request-a-quote" },
];

export const SiteFooter = () => {
  const theme = themeFor(useLocation().pathname);
  const icon = theme === "pan" ? LOGO.iconPan : theme === "blue" ? LOGO.iconBlue : LOGO.iconDark;
  const word = theme === "pan" ? LOGO.wordPan : theme === "blue" ? LOGO.wordBlue : LOGO.wordDark;
  return (
  <footer className={`${theme === "pan" ? "bg-nx-black" : "bg-[#060d1a]"} border-t border-white/10 pt-16 pb-40 sm:pb-32`} data-testid="site-footer">
    <div className="nx-wrap">
      <div className="grid md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1.3fr] gap-10 lg:gap-8">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <img src={icon} alt="" className="h-11 w-auto" />
            <img src={word} alt="NexSecure Technologies" className="h-4 w-auto" />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xs">
            Integrated Technology Solutions & IT Supply Company. Engineering Intelligent Infrastructure across Kerala.
          </p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-5">Solutions</p>
          <ul className="flex flex-col gap-2.5">
            {SOLUTION_DETAILS.slice(0, 6).map((s) => (
              <li key={s.slug}><Link to={`/solutions/${s.slug}`} className="text-sm text-slate-400 hover:text-nx-goldlight transition-colors">{s.name}</Link></li>
            ))}
            <li><Link to="/solutions" className="text-sm text-nx-goldlight hover:text-white transition-colors">All solutions →</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-5">Services</p>
          <ul className="flex flex-col gap-2.5">
            {SERVICES.slice(0, 9).map((s) => (
              <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-sm text-slate-400 hover:text-nx-goldlight transition-colors">{s.name}</Link></li>
            ))}
            <li><Link to="/services" className="text-sm text-nx-goldlight hover:text-white transition-colors">All services →</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-5">Company</p>
          <ul className="flex flex-col gap-2.5">
            {COMPANY.map((c) => (
              <li key={c.to}><Link to={c.to} className="text-sm text-slate-400 hover:text-nx-goldlight transition-colors" data-testid={`footer-company-${c.to.slice(1)}`}>{c.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-slate-500 mb-5">Contact</p>
          <ul className="flex flex-col gap-4 text-sm text-slate-400">
            <li className="flex items-start gap-3"><Phone size={15} className="text-nx-goldlight mt-0.5 shrink-0" /><span><a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="block hover:text-nx-goldlight transition-colors" data-testid="footer-phone">{CONTACT.phone}</a><a href={`tel:${CONTACT.phone2.replace(/\s/g, "")}`} className="block hover:text-nx-goldlight transition-colors">{CONTACT.phone2}</a></span></li>
            <li className="flex items-start gap-3"><TelegramIcon size={15} className="text-[#229ED9] mt-1 shrink-0" /><span><TelegramButton testid="footer-telegram" /></span></li>
            <li className="flex items-start gap-3"><Mail size={15} className="text-nx-goldlight mt-0.5 shrink-0" /><span><a href={`mailto:${CONTACT.info}`} className="block hover:text-nx-goldlight transition-colors" data-testid="footer-email">{CONTACT.info}</a><a href={`mailto:${CONTACT.support}`} className="block hover:text-nx-goldlight transition-colors">{CONTACT.support}</a></span></li>
            <li className="flex items-start gap-3"><MapPin size={15} className="text-nx-goldlight mt-0.5 shrink-0" /><span className="leading-relaxed">{CONTACT.addressLines.map((l) => <span key={l} className="block">{l}</span>)}<a href={CONTACT.mapsLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-nx-goldlight hover:text-white transition-colors mt-2" data-testid="footer-maps-link">View on Google Maps <ExternalLink size={12} /></a><span className="block text-xs text-slate-500 mt-2">{CONTACT.hours}</span></span></li>
          </ul>
        </div>
      </div>
      <div className="mt-14 pt-7 border-t border-white/5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <p className="text-xs text-slate-600">© {new Date().getFullYear()} NexSecure Technologies · Ernakulam, Kerala, India</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {LEGAL_ORDER.map((s) => (
            <Link key={s} to={`/legal/${s}`} className="text-xs text-slate-600 hover:text-nx-goldlight transition-colors" data-testid={`footer-legal-${s}`}>{LEGAL_PAGES[s].title}</Link>
          ))}
        </div>
      </div>
    </div>
  </footer>
  );
};
